
import React from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Button } from '@/components/ui/button';
import { CalendarIcon, Clock, User } from 'lucide-react';
import { useLanguage } from '@/context/language-context';

const blogPosts = [
  {
    id: 1,
    title: "How to Stay Safe While Dating Online",
    excerpt: "Discover the essential safety tips everyone should follow when meeting someone from an online platform.",
    image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=800&q=80",
    date: "May 15, 2023",
    readTime: "5 min read",
    author: "Emma Johnson",
    category: "Safety"
  },
  {
    id: 2,
    title: "Premium Features: Are They Worth It?",
    excerpt: "An honest review of premium features on dating platforms and whether they provide real value.",
    image: "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?auto=format&fit=crop&w=800&q=80",
    date: "April 28, 2023",
    readTime: "7 min read",
    author: "Michael Chen",
    category: "Premium"
  },
  {
    id: 3,
    title: "Finding Genuine Connections in a Digital Age",
    excerpt: "Tips for creating authentic relationships despite the challenges of modern online dating.",
    image: "https://images.unsplash.com/photo-1649972904349-6e44c42644a7?auto=format&fit=crop&w=800&q=80",
    date: "June 10, 2023",
    readTime: "6 min read",
    author: "Sophia Garcia",
    category: "Relationships"
  },
  {
    id: 4,
    title: "How to Create an Attractive Profile",
    excerpt: "Expert advice on creating a profile that stands out and attracts the right attention.",
    image: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=800&q=80",
    date: "March 22, 2023",
    readTime: "4 min read",
    author: "James Wright",
    category: "Profile Tips"
  }
];

const Blog = () => {
  const { t } = useLanguage();
  
  return (
    <Layout>
      <div className="container mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 bg-pink-gradient text-gradient">{t('blog.title')}</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('blog.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map((post) => (
            <Card key={post.id} className="overflow-hidden transition-all hover:shadow-lg">
              <div className="overflow-hidden">
                <AspectRatio ratio={16 / 9}>
                  <img 
                    src={post.image} 
                    alt={post.title} 
                    className="object-cover w-full h-full transition-transform hover:scale-105"
                  />
                </AspectRatio>
              </div>
              <CardHeader>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-medium bg-purple-100 text-purple-800 px-2 py-1 rounded">
                    {post.category}
                  </span>
                </div>
                <CardTitle className="hover:text-pink-500 transition-colors">
                  {post.title}
                </CardTitle>
                <CardDescription>{post.excerpt}</CardDescription>
              </CardHeader>
              <CardFooter className="flex flex-col items-start">
                <div className="flex items-center text-sm text-gray-500 mb-4 w-full">
                  <div className="flex items-center mr-4">
                    <CalendarIcon size={14} className="mr-1" />
                    <span>{post.date}</span>
                  </div>
                  <div className="flex items-center mr-4">
                    <Clock size={14} className="mr-1" />
                    <span>{post.readTime}</span>
                  </div>
                  <div className="flex items-center">
                    <User size={14} className="mr-1" />
                    <span>{post.author}</span>
                  </div>
                </div>
                <Button variant="outline" className="w-full hover:bg-pink-50 hover:text-pink-600 transition-colors">
                  {t('blog.read_article')}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        <div className="text-center mt-12">
          <Button className="bg-pink-gradient hover:opacity-90 transition-opacity">
            {t('blog.load_more')}
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default Blog;
